import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    IconButton,
    Tooltip 
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import { Modal, Table as AntTable, Button } from "antd";
import { v4 as uuidv4 } from "uuid";
import { useSnackbar } from "notistack";
import DescriptionIcon from "@mui/icons-material/Description";
import { dataModelExecutionDetailsById } from "../../../api's/DataModelApi";
import { fetchSampleRecordsApi } from "../../../api's/DataProcessorApi";
import { AWS_CONFIG_TYPE } from "../../../BaseUrl";

const DataModelExecutionTab = ({ dataModelExecution, loading, error, onRefreshRow }) => {
    const [loadingId, setLoadingId] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const [sampleViewModalOpen, setSampleViewModalOpen] = useState(false);
    const [sampleViewHeader, setSampleViewHeader] = useState([]);
    const [sampleViewData, setSampleViewData] = useState([]);
    const [isFetchingSample, setIsFetchingSample] = useState(false);

    if (loading) return <CircularProgress />;
    if (error) return <p style={{ color: "red" }}>Error: {error}</p>;

    const handleRefresh = async (executionId) => {
        setLoadingId(executionId);
        try {
            const res = await dataModelExecutionDetailsById(executionId);
            onRefreshRow(executionId, res.data);
            enqueueSnackbar("Execution details refreshed successfully!", {
                variant: "success",
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
            });
        } catch (err) {
            console.error("Failed to refresh execution:", err);
            enqueueSnackbar("Failed to refresh execution!", {
                variant: "error",
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
            });
        } finally {
            setLoadingId(null);
        }
    };

    const fetchSampleView = async (path) => {
        enqueueSnackbar("Fetching sample records...", {
            variant: "info",
            autoHideDuration: 2000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setIsFetchingSample(true);
        setSampleViewHeader([]);
        setSampleViewData([]);

        if (!path.trim()) {
            enqueueSnackbar("Failed to fetch sample view!", {
                variant: "error",
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
            });
            setIsFetchingSample(false);
            return;
        }

        const data = {
            client_id: "client_123",
            workflow_id: "workflow_123",
            csvPath: path,
            aws_config_type: AWS_CONFIG_TYPE,
        };

        try {
            const response = await fetchSampleRecordsApi(data);
            console.log("Sample Records Response:", response);

            if (!response.data || Object.keys(response.data).length === 0) {
                enqueueSnackbar("No sample data found. Run the flow first.", {
                    variant: "warning",
                    autoHideDuration: 3000,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                });
                setIsFetchingSample(false);
                return;
            }

            // Prepare columns
            const headers = response.data.headers.map((el) => ({
                title: el.title,
                dataIndex: el.dataIndex,
                key: el.dataIndex,
            }));

            // Prepare rows
            const rows = response.data.data.map((el) => ({
                id: uuidv4(),
                ...el,
            }));

            setSampleViewHeader(headers);
            setSampleViewData(rows);
            setSampleViewModalOpen(true);
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Failed to fetch sample view!", {
                variant: "error",
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
            });
        } finally {
            setIsFetchingSample(false);
        }
    };

    return (
        <>
            {/* TABLE OF EXECUTIONS */}
            <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Pipeline Name</b></TableCell>
                            <TableCell><b>Started At</b></TableCell>
                            <TableCell><b>Completed At</b></TableCell>
                            <TableCell><b>Status</b></TableCell>
                            <TableCell><b>Actions</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataModelExecution.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No execution history found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            dataModelExecution.map((exec) => (
                                <TableRow key={exec.id}>
                                    <TableCell>{exec.pipelineName}</TableCell>
                                    <TableCell>
                                        {exec.startedAt
                                            ? new Date(exec.startedAt).toLocaleString()
                                            : "-"}
                                    </TableCell>
                                    <TableCell>
                                        {exec.completedAt
                                            ? new Date(exec.completedAt).toLocaleString()
                                            : "-"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            color:
                                                exec.status === "completed"
                                                    ? "green"
                                                    : exec.status === "failed"
                                                    ? "red"
                                                    : "orange",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {exec.status}
                                       <Tooltip title="Refresh">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRefresh(exec.id)}
                                                sx={{ p: 0.5 }}
                                            >
                                                <RefreshIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => fetchSampleView(exec.csv_path)}
                                            disabled={isFetchingSample}
                                        >
                                            <DescriptionIcon style={{ fontSize: "20px" }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* SAMPLE VIEW MODAL */}
            <Modal
                title="Sample Data View"
                open={sampleViewModalOpen}
                onCancel={() => setSampleViewModalOpen(false)}
                footer={null}
                width="90%"
                centered
                bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
            >
                {isFetchingSample ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <CircularProgress />
                        <p>Loading sample data...</p>
                    </div>
                ) : (
                    <AntTable
                        columns={sampleViewHeader}
                        dataSource={sampleViewData}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: true }}
                        rowKey="id"
                    />
                )}
            </Modal>
        </>
    );
};

DataModelExecutionTab.propTypes = {
    dataModelExecution: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRefreshRow: PropTypes.func.isRequired,
};

export default DataModelExecutionTab;
